import { useEffect, useMemo, useState } from "react";
// Edición inline del nombre de etiqueta
function EditLabel({ userId, current, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(userId)}/label`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cajaLabel: val.trim() || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      onSaved(body);
      setEditing(false);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (!editing) return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 13, color: current ? "#111" : "#aaa", fontStyle: current ? "normal" : "italic" }}>
        {current || "sin etiqueta"}
      </span>
      <button onClick={() => { setVal(current || ""); setEditing(true); }}
        style={{ border: "none", background: "none", cursor: "pointer", opacity: 0.45, fontSize: 13, padding: "2px 4px" }}>✏️</button>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <input value={val} onChange={e => setVal(e.target.value)} autoFocus
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        style={{ width: 130, padding: "4px 8px", border: "1px solid var(--brand-red)", borderRadius: 6, fontSize: 13 }} />
      <button onClick={save} disabled={saving} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 16 }}>✓</button>
      <button onClick={() => setEditing(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 16, opacity: 0.5 }}>✕</button>
    </div>
  );
}



function PopupFusion({ user, onClose, onDone }) {
  const [cajas, setCajas] = useState([]);
  const [mergeCaja, setMergeCaja] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/cajas/list-numbers").then(r => r.json()).then(d => {
      setCajas(Array.isArray(d) ? d.filter(c => c.caja !== user.caja) : []);
    }).catch(() => {});
  }, [user.caja]);

  const save = async () => {
    if (!mergeCaja) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(user.id)}/caja`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mergeCaja: Number(mergeCaja) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      onDone(body);
    } catch (e) { alert(e.message); setSaving(false); }
  };

  const mergedTipo = cajas.find(c => String(c.caja) === String(mergeCaja))?.cajaTipo;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:"#fff", borderRadius:12, padding:28, minWidth:320, maxWidth:400 }}>
        <h3 style={{ margin:"0 0 8px" }}>🔗 Fusionar caja</h3>
        <p style={{ margin:"0 0 16px", fontSize:13, opacity:0.7 }}>
          Fusionar <strong>{user.cajaLabel || user.displayName}</strong> (caja {user.caja}) con otra caja existente.
          El usuario pasará a compartir caja con los miembros de destino.
        </p>
        <select value={mergeCaja} onChange={e => setMergeCaja(e.target.value)}
          style={{ width:"100%", padding:"8px 10px", borderRadius:6, border:"1px solid #ccc", fontSize:14, marginBottom:8 }}>
          <option value="">— Selecciona caja destino —</option>
          {cajas.map(c => (
            <option key={c.caja} value={c.caja}>
              Caja {c.caja} · {c.cajaTipo} · {c.members?.map(m => m.cajaLabel || m.displayName).join(", ")}
            </option>
          ))}
        </select>
        {mergeCaja && <div style={{ fontSize:12, color:"#666", marginBottom:12 }}>
          Se añadirá como <strong>{mergedTipo}</strong> en la caja {mergeCaja}
        </div>}
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ padding:"8px 16px", borderRadius:6, border:"1px solid #ccc", background:"#fff", cursor:"pointer" }}>Cancelar</button>
          <button onClick={save} disabled={saving || !mergeCaja}
            style={{ padding:"8px 16px", borderRadius:6, border:"none", background:"#c0392b", color:"#fff", cursor: (!mergeCaja||saving)?"not-allowed":"pointer", opacity:(!mergeCaja||saving)?0.6:1 }}>
            {saving ? "Guardando…" : `🔗 Fusionar en caja ${mergeCaja}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Usuarios() {
  const [users, setUsers] = useState([]);
  const [noCajaUsers, setNoCajaUsers] = useState([]);
  const [loadingNoCaja, setLoadingNoCaja] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [deleting, setDeleting] = useState({}); // { [id]: true }
  const [fusionUser, setFusionUser] = useState(null);
  const [query, setQuery] = useState("");

  const loadUsers = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch("/api/users")

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const loadNoCajaUsers = async () => {
    setLoadingNoCaja(true);
    setErr("");
    try {
      const res = await fetch(`/api/users/sin-caja`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setNoCajaUsers(data);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoadingNoCaja(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadUsers(), loadNoCajaUsers()]);
  };


  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const deleteUser = async (id) => {
    const ok = window.confirm("¿Seguro que quieres borrar este usuario?");
    if (!ok) return;

    setDeleting((prev) => ({ ...prev, [id]: true }));
    setErr("");

    try {
      const res = await fetch(`/api/users/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      setUsers((prev) => prev.filter((u) => String(u.id) !== String(id)));
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setDeleting((prev) => ({ ...prev, [id]: false }));
    }
  };

  // ---- BÚSQUEDA ----
  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;

    return users.filter((u) => {
      const displayName = String(u.displayName || "").toLowerCase();
      const id = String(u.id || "").toLowerCase();
      const lastUniqueId = String(u.lastKnownUniqueId || "").toLowerCase(); // @
      const lastNickname = String(u.lastKnownNickname || "").toLowerCase();

      return (
        displayName.includes(q) ||
        id.includes(q) ||
        lastUniqueId.includes(q) ||
        lastNickname.includes(q)
      );
    });
  }, [users, query]);

  const assignCaja = async (id, tipo) => {
    setErr("");
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(id)}/caja`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tipo }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);

      // IMPORTANTÍSIMO: refrescar ambas listas para que "suba/baje"
      await refreshAll();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  };


  const clearSearch = () => setQuery("");

  return (
    <div style={{ margin: "30px 24px", padding: 0 }}>
      

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
        <h2 style={{ margin: 0 }}>Usuarios guardados</h2>

        <button onClick={refreshAll} disabled={loading || loadingNoCaja}>
          {loading || loadingNoCaja ? "Cargando..." : "Refrescar"}
        </button>


        <div style={{ marginLeft: "auto", opacity: 0.7 }}>
          Mostrando: {filteredUsers.length} / Total: {users.length}
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, ID, @ o alias..."
          style={{
            flex: 1,
            padding: 10,
            border: "1px solid #ddd",
            borderRadius: 8,
          }}
        />

        <button onClick={clearSearch} disabled={!query.trim()}>
          Limpiar
        </button>
      </div>

      {err && (
        <div style={{ marginTop: 12, padding: 10, border: "1px solid #f99", color: "#900" }}>
          Error: {err}
        </div>
      )}

      {noCajaUsers.length > 0 && (
        <div style={{ marginTop: 16, border: "2px solid #111", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ background: "#111", color: "white", padding: 10, fontWeight: "bold" }}>
            Usuarios sin caja ({noCajaUsers.length})
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th style={th}>Nombre</th>
                <th style={th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {noCajaUsers.map((u) => (
                <tr key={u.id}>
                  <td style={td}>{u.displayName}</td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button onClick={() => assignCaja(u.id, "enviar")} style={{ padding: "6px 10px" }}>
                        Enviar
                      </button>
                      <button onClick={() => assignCaja(u.id, "recoger")} style={{ padding: "6px 10px" }}>
                        Recoger
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ padding: 10, fontSize: 12, opacity: 0.75 }}>
            Enviar asigna caja 1–100 (primera libre). Recoger asigna desde 101 hacia arriba (primera libre).
          </div>
        </div>
      )}



      <div style={{ marginTop: 16, border: "1px solid #ddd", borderRadius: 8, overflowX: "auto", width: "100%" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto", whiteSpace: "nowrap" }}>
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={{...th, width: 48}}>Foto</th>
              <th style={{...th, minWidth: 150}}>Nombre (app)</th>
              <th style={{...th, minWidth: 150}}>Último @</th>
              <th style={{...th, minWidth: 150}}>Último alias</th>
              <th style={{...th, width: 60, textAlign:"center"}}>Caja</th>
              <th style={{...th, width: 90}}>Tipo</th>
              <th style={{...th, minWidth: 180}}>Etiqueta caja</th>
              <th style={{...th, minWidth: 130, textAlign:"center"}}>Acciones</th>
            </tr>
          </thead>


          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id}>
                <td style={td}>
                  {u.profilePictureUrl ? (
                    <img
                      src={u.profilePictureUrl}
                      alt="perfil"
                      style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
                    />
                  ) : (
                    <span style={{ opacity: 0.5 }}>—</span>
                  )}
                </td>

                <td style={td}>{u.displayName}</td>
                <td style={td}>@{u.lastKnownUniqueId || "—"}</td>
                <td style={td}>{u.lastKnownNickname || "—"}</td>

                <td style={td}>
                  {u.caja ?? <span style={{ opacity: 0.5 }}>—</span>}
                </td>

                <td style={td}>
                  {u.cajaTipo ? (
                    <span style={{ textTransform: "capitalize" }}>{u.cajaTipo}</span>
                  ) : (
                    <span style={{ opacity: 0.5 }}>—</span>
                  )}
                </td>

                <td style={td}>
                  {u.caja != null ? (
                    <EditLabel userId={u.id} current={u.cajaLabel || null}
                      onSaved={(updated) => setUsers(prev => prev.map(x => x.id === u.id ? updated : x))} />
                  ) : <span style={{ opacity: 0.4, fontSize: 12 }}>sin caja</span>}
                </td>
                <td style={{ ...td, textAlign: "center" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                    {u.caja != null && (
                      <button onClick={() => setFusionUser(u)} title="Fusionar con otra caja"
                        style={{ padding: "4px 10px", fontSize: 12, border: "1px solid #888", background: "white", cursor: "pointer", borderRadius: 4 }}>
                        🔗
                      </button>
                    )}
                    <button
                      onClick={() => deleteUser(u.id)}
                      disabled={!!deleting[u.id]}
                      title="Borrar usuario"
                      style={{
                        padding: "4px 10px", fontSize: 12,
                        border: "1px solid #d33", background: "white", color: "#d33",
                        cursor: deleting[u.id] ? "not-allowed" : "pointer", borderRadius: 4,
                      }}
                    >
                      {deleting[u.id] ? "..." : "Borrar"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {!loading && filteredUsers.length === 0 && (
              <tr>
                <td style={{ padding: 14, textAlign: "center" }} colSpan={9}>
                  {query.trim()
                    ? "No hay resultados para esa búsqueda."
                    : "No hay usuarios guardados aún."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {fusionUser && (
        <PopupFusion
          user={fusionUser}
          onClose={() => setFusionUser(null)}
          onDone={(updated) => {
            setUsers(prev => prev.map(u => u.id === fusionUser.id ? updated : u));
            setFusionUser(null);
          }}
        />
      )}
    </div>
  );
}

const th = { textAlign: "left", padding: "10px 14px", borderBottom: "1px solid #ddd", whiteSpace: "nowrap" };
const td = { padding: "10px 14px", borderBottom: "1px solid #eee", verticalAlign: "middle" };

